import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useApi } from "../../../api/ApiProvider";
import Api from "../../../axiosApi/api";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import { ButtonWithOptions } from "components/common/ButtonWithOptions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import EditIcon from "@mui/icons-material/Edit";
import RequiredField from "../../common/RequiredField";
import intl from "components/i18n/ReactIntlWrapper";
import Spinner from "../../Spinner";
import { handleAPIError } from "../../../common/errorHandler";
import CorporateEntityForm from "./CorporateEntityForm";
import AddressCompact from "../../Address/AddressCompact";
import { confirmAlert } from "react-confirm-alert";
import { CreateCorporateEntityCommand } from "axiosApi/models/create-corporate-entity-command";
import { UpdateCorporateEntityCommand } from "axiosApi/models/update-corporate-entity-command";
import BillingTable from "components/Billings/BillingTable";
import InvoiceConfigurationTable from "components/InvoiceConfiguration/InvoiceConfigurationTable";
import { ENTITY_TYPE } from "common/constants";
import {
  Divider,
  Typography,
  Box,
  Button,
  Stack,
  MenuItem,
  Card,
  CardContent
} from '@mui/material';

const CorporateEntityDetail = () => {
  const DefaultCorporateEntityDetailInput = {
    id: null,
    name: "",
    country: { name: "", labelKey: "", code: "" },
    idCountry: null,
    active: true,
    corporateEntityAddresses: [],
    personas: [],
  };

  const [input, setInput] = useState(DefaultCorporateEntityDetailInput);

  const api: Api = useApi();

  const navigate = useNavigate();

  const toast = useToastMessageQueue();

  const { id } = useParams();
  const idNumber = parseInt(id);

  const [loading, setLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState({});

  const [disabled, setDisabled] = useState(false);

  const [disabledNew, setDisabledNew] = useState(false);

  const [personaExist, setPersonaExist] = useState<boolean>(false);

  const [realAddressesId, setRealAddressesId] = useState<number[]>([]);
  const [legalAddressesId, setLegalAddressesId] = useState<number[]>([]);


  const [countries, setCountries] = useState(null);
  console.log({ errors,realAddressesId,legalAddressesId });

  const fetchCorporateEntityDetail = async (idNumber: number) => {
    setLoading(true);
    const response = await api.invoiceApi
      .apiVversionCorporateEntityIdGet(idNumber, "1", {})
      .then((response) => {
        if (response.data.data) {
          setInput({
            ...input,
            id: response.data?.data?.id,
            name: response.data?.data?.name,
            country: {
              name: response.data?.data?.country?.name,
              labelKey: response.data?.data?.country?.labelKey,
              code: response.data?.data?.country?.code,
            },
            idCountry: response.data?.data?.idCountry,
            active: response.data?.data?.active,
            corporateEntityAddresses:
              response.data?.data?.corporateEntityAddresses,
          });
          const matchingRealAddressIds = response.data?.data?.corporateEntityAddresses
            ?.filter(add => add?.address?.idAddressType === 1)
            .map(add => add.address.id);
          setRealAddressesId(matchingRealAddressIds);
          const matchingLegalAddressIds = response.data?.data?.corporateEntityAddresses
            ?.filter(add => add?.address?.idAddressType === 2)
            .map(add => add.address.id);
          setLegalAddressesId(matchingLegalAddressIds);

        }
        setPersonaExist(false);
        setLoading(false);
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
        setLoading(false);
      });
  };

  const fetchCountries = async () => {
    const response = await api.addressApi
      .apiVversionAddressCountryAllGet("1", {})
      .then((response) => {
        if (response.data) {
          setCountries(response.data?.data);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  useEffect(() => {
    if (idNumber) {
      fetchCorporateEntityDetail(idNumber);
      setDisabled(true);
    }
    setDisabledNew(true);
    fetchCountries();
  }, [idNumber]);

  useEffect(() => {
    const isInputDirty =
      JSON.stringify(input) !==
      JSON.stringify(DefaultCorporateEntityDetailInput);

    if (isInputDirty) {
      setErrors(validate(input));
    }
  }, [legalAddressesId, realAddressesId, input]);

  const handleInputChange = function (e) {
    setInput({
      ...input,
      [e.target.name]: e.target.value,
    });
    setErrors(
      validate({
        ...input,
        [e.target.name]: e.target.value,
      })
    );
  };

  const handleCountryChange = (e) => {
    const inputVal = {
      ...input,
      idCountry: e.id,
      country: {
        ...e.country,
        name: intl.get(e.labelKey),
        labelKey: e.labelKey,
      },
    };
    setInput({ ...inputVal });
    setErrors(
      validate({
        ...input,
        idCountry: e.id,
      })
    );
  };

  const validate = function (input) {
    setDisabledNew(false);
    let errors: any = {};
    if (!input.name) {
      errors.name = intl.get("validate.errors.isRequired");
    }
    if (!input.idCountry) {
      errors.idCountry = intl.get("validate.errors.isRequired");
    }

    const addedAddresses = realAddressesId.length > 0 || legalAddressesId.length > 0;

    if (!addedAddresses) {
      errors.address = intl.get("validate.errors.isRequired");
    }

    return errors;
  };

  const handleBackClick = (e) => {
    navigate("/corporateEntities");
    setPersonaExist(false);
  };

  const handleSaveClick = () => {
    handleSubmit(true);
  };

  const handleSaveCloseClick = () => {
    handleSubmit(false);
  };

  const handleEditMode = (e) => {
    e.preventDefault();
    if (idNumber) {
      setPersonaExist(false);
      fetchCorporateEntityDetail(idNumber);
    }
    setErrors({});
    setDisabled(!disabled);
  };

  const handleStatusChange = (e) => {
    const inputVal = {
      ...input,
      active: e.currentTarget.checked,
    };
    setInput({ ...inputVal });
  };

  const handlePersonaChange = (event) => {
    setInput((prevInput) => ({
      ...prevInput,
      personas: event,
    }));
  };

  const handleDelete = () => {
    confirmAlert({
      title: intl.get("delete.modal.title"),
      message:
        "The corporate entity will be deleted. Are you sure you want to perform this action?",
      buttons: [
        {
          label: intl.get("delete.modal.cancel.button"),
          onClick: () => {},
        },
        {
          label: intl.get("delete.modal.delete.button"),
          onClick: async () => {
            const response = await api.invoiceApi
              .apiVversionCorporateEntityIdDelete(idNumber, "1", {})
              .then((response) => {
                toast.success({ body: "Corporate entity deleted!" });
                navigate("/corporateEntities");
              })
              .catch((error) => {
                handleAPIError(error, toast, errors);
                setErrors({ ...errors });
              });
          },
        },
      ],
    });
  };

  const handleSubmit = async (saveAndEdit: boolean = false) => {
    setLoading(true);
    const errors = validate(input);
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      if (idNumber) {
        let addressIds = [...legalAddressesId, ...realAddressesId];
        let personaIds = input.personas;
        let cmd: UpdateCorporateEntityCommand = {
          id: idNumber,
          name: input.name,
          idCountry: input.idCountry,
          active: input.active,
          ...(addressIds.length > 0 && { addressIds }),
          personaIds: personaIds,
        };
        const response = await api.invoiceApi
          .apiVversionCorporateEntityPut("1", cmd, {})
          .then((response) => {
            if (saveAndEdit) {
              setDisabled(true);
              setPersonaExist(false);
            } else {
              navigate("/corporateEntities");
            }
            toast.success({ body: "Corporate entity has been updated!" });
            setLoading(false);
          })
          .catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
          });
      } else {
        let addressIds = [...legalAddressesId, ...realAddressesId];
        let cmd: CreateCorporateEntityCommand = {
          name: input.name,
          idCountry: input.idCountry,
          active: input.active,
          addressIds: addressIds,
        };
        const response = await api.invoiceApi
          .apiVversionCorporateEntityPost("1", cmd, {})
          .then((response) => {
            toast.success({ body: "Corporate entity has been added!" });
            setLoading(false);
            if (saveAndEdit && "data" in response.data)
              navigate(`/corporateEntities/detail/${response.data.data.id}`);
            else navigate("/corporateEntities");
          })
          .catch((error) => {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
          });
      }
    }
    setLoading(false);
  };

  return (
   <Box p={3}>
            <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
              {idNumber
                ? intl.get('corporateEntity.detail')
                : intl.get('corporateEntity.new')}
            </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <Spinner />
          </Box>
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <CorporateEntityForm
                  errors={errors}
                  input={input}
                  handleInputChange={handleInputChange}
                  disabled={disabled}
                  handleStatusChange={handleStatusChange}
                  countries={countries}
                  handleCountryChange={handleCountryChange}
                />
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <AddressCompact
                  addressesId={realAddressesId}
                  setAddressesId={setRealAddressesId}
                  disabled={disabled}
                  identifier="ui.corporateentities.detail.edit-gate"
                  allowOne={true}
                  realAddress={true}
                />
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <AddressCompact
                  addressesId={legalAddressesId}
                  setAddressesId={setLegalAddressesId}
                  disabled={disabled}
                  identifier="ui.corporateentities.detail.edit-gate"
                  allowOne={true}
                  legalAddress={true}
                />
              </CardContent>
            </Card>

            {!isNaN(idNumber) && input.idCountry && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <InvoiceConfigurationTable
                    corporateEntityId={idNumber}
                    countryCode={input.country?.code?.toUpperCase() || ''}
                    disabled={disabled}
                  />
                </CardContent>
              </Card>
            )}

            {!isNaN(idNumber) && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <BillingTable
                    entityId={idNumber}
                    idEntityType={ENTITY_TYPE.CORPORATE_ENTITY}
                    disabled={disabled}
                    billToDisplay={false}
                  />
                </CardContent>
              </Card>
            )}

            <Box mt={4}>
              <RequiredField />
            </Box>

            <Stack direction="row" justifyContent="flex-end" spacing={2} mt={3}>
              {isNaN(idNumber) && !disabled && (
                <>
                  {loading === true ? (
                    <Spinner />
                  ) : (
                    <>
                      <Button
                        onClick={handleBackClick}
                      >
                        <ArrowBackIcon />
                      </Button>
                      <ButtonWithOptions
                        menu={<>
                              <MenuItem onClick={handleSaveCloseClick}>{intl.get('save&close.button')}</MenuItem>
                        </>}
                        disabled={
                          Object.keys(errors).length > 0 || disabledNew
                        }
                        className="btn btn-primary"
                        onClick={handleSaveClick}
                      >
                        <SaveOutlinedIcon className="mb-1" />{' '}
                        {intl.get('save.button')}
                      </ButtonWithOptions>
                    </>
                  )}
                </>
              )}

              {!isNaN(idNumber) && disabled && (
                <>
                  <Button
                    onClick={handleBackClick}
                  >
                    <ArrowBackIcon />
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEditMode}
                  >
                    {intl.get('edit.button')}
                  </Button>
                </>
              )}

              {!isNaN(idNumber) && !disabled && (
                <>
                  <Button variant="outlined" onClick={handleEditMode}>
                    {intl.get('cancel.button')}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                  >
                    {intl.get('delete.button')}
                  </Button>
                  <ButtonWithOptions
                    menu={<>
                            <MenuItem onClick={handleSaveCloseClick}>{intl.get('save&close.button')}</MenuItem>
                      </>}
                    disabled={Object.keys(errors).length > 0 || disabled}
                    className="btn btn-primary"
                    onClick={handleSaveClick}
                  >
                    <SaveOutlinedIcon className="mb-1" />{' '}
                    {intl.get('save.button')}
                  </ButtonWithOptions>
                </>
              )}
            </Stack>
          </>
        )}
    </Box>
  );
};

export default CorporateEntityDetail;
